import { useQuery } from '@tanstack/react-query';
import { useDialogA11y } from '../../lib/use-dialog-a11y.js';
import { Calendar, Loader2, MapPin, Phone, User, Users, X } from 'lucide-react';
import { api } from '../../lib/api.js';
import {
  durationLabel,
  windowLabel,
  FOP_UDF,
  readAssignment,
  readHelpers,
  useTechNames,
} from '../../lib/dispatch-board.js';
import { normalizeTrade, orderTrade, tradeLabel, useSalesTypes } from '../../lib/trade.js';
import { useFieldJobsSettings } from '../../lib/settings.js';
import { JobFiles } from '../job-files.js';
import { jobSite } from '../../lib/job-site.js';
import { JobNotes } from '../job-notes.js';

interface JobPreviewModalProps {
  /** Spire id of the order to preview. */
  spireId: number;
  /** Order number for the header (avoids waiting on detail to render). */
  orderNo: string;
  /** Customer name shown while the detail loads. */
  customerNameFallback: string;
  onClose: () => void;
  /** Optional — wired when invoked from a card so "Start job" can advance
   * the tech into the regular fresh-start flow without losing context. */
  onStart?: () => void;
}

/**
 * Read-only job peek for the Day list. Fetches the SO detail (cached via
 * TanStack Query so re-opening is instant), and renders customer / address
 * / scheduled window / parts list / total without starting the timer or
 * grabbing the active-job lock.
 *
 * `onStart` is optional — when provided, the modal surfaces a "Start job"
 * CTA that closes the preview and delegates to the parent's regular
 * fresh-start flow (which goes through the confirm sheet). When omitted
 * (e.g. done cards), only the read-only details show.
 */
export function JobPreviewModal({
  spireId,
  orderNo,
  customerNameFallback,
  onClose,
  onStart,
}: JobPreviewModalProps) {
  const detailQuery = useQuery({
    queryKey: ['sales-orders', 'detail', spireId],
    queryFn: ({ signal }) => api.salesOrders.getById(spireId, signal),
    staleTime: 30_000,
  });
  const detail = detailQuery.data;
  const customerName = detail?.customer.name ?? customerNameFallback;

  const techNameById = useTechNames();

  const salesTypes = useSalesTypes();
  const typeUdfKey = useFieldJobsSettings().typeUdfKey;
  const trade = detail ? orderTrade(salesTypes, detail, typeUdfKey) : normalizeTrade(salesTypes, null);
  const assignment = detail ? readAssignment(detail.udf ?? {}) : null;
  const leadId = detail?.udf?.[FOP_UDF.tech]?.trim() ?? null;
  const helperIds = detail ? readHelpers(detail.udf ?? {}) : [];
  const scheduledLabel = assignment ? formatWindow(assignment.startHour, assignment.duration) : null;
  // Ship-to = the service address (lib/job-site.ts).
  const site = detail ? jobSite(detail) : null;
  const phoneNumber = detail?.contactPhone ? detail.contactPhone.replace(/[^\d+]/g, '') : null;
  const partsTotal = detail?.items.reduce(
    (sum, line) => sum + Number(line.orderQty) * Number(line.unitPrice),
    0,
  );

  const dialogRef = useDialogA11y<HTMLDivElement>(onClose);
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Preview ${orderNo}`}
      className="fixed inset-0 z-40 bg-slate-950/40 flex items-end sm:items-center justify-center p-3"
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md max-h-[85vh] flex flex-col rounded-xl shadow-xl overflow-hidden"
        style={{ backgroundColor: 'var(--color-elevated-surface)' }}
      >
        <header
          className="flex items-start justify-between gap-2 px-4 py-3 border-b"
          style={{ borderColor: 'var(--color-border-tertiary)' }}
        >
          <div className="min-w-0">
            <div className="text-sm font-semibold text-ink truncate">{customerName}</div>
            <div className="text-[11px] text-ink-muted">
              <span className="font-mono">{orderNo}</span>
              {' · '}
              {tradeLabel(salesTypes, trade)}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close preview"
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-ink-muted hover:bg-surface-muted hover:text-ink shrink-0"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          {detailQuery.isPending && (
            <div className="flex items-center justify-center py-6 text-ink-muted text-sm">
              <Loader2 className="h-4 w-4 animate-spin mr-2" aria-hidden />
              Loading job details…
            </div>
          )}
          {detailQuery.isError && (
            <div className="text-sm text-red-700">
              Couldn't load this order: {detailQuery.error.message}
            </div>
          )}
          {detail && (
            <>
              <dl className="grid grid-cols-[20px_auto_1fr] gap-x-2 gap-y-1.5 text-sm">
                {scheduledLabel && (
                  <PreviewRow
                    Icon={Calendar}
                    label="Scheduled"
                    value={scheduledLabel}
                  />
                )}
                {site?.contactName && (
                  <PreviewRow Icon={User} label="Ship-to" value={site.contactName} />
                )}
                {site && site.addressLines.length > 0 && (
                  <PreviewRow
                    Icon={MapPin}
                    label="Site"
                    value={site.addressLines.join(' · ')}
                  />
                )}
                {detail.contactName && (
                  <PreviewRow Icon={User} label="Contact" value={detail.contactName} />
                )}
                {phoneNumber && (
                  <PreviewRow
                    Icon={Phone}
                    label="Phone"
                    value={detail.contactPhone ?? phoneNumber}
                    href={`tel:${phoneNumber}`}
                  />
                )}
                {(leadId || helperIds.length > 0) && (
                  <PreviewRow
                    Icon={Users}
                    label="Crew"
                    value={
                      [
                        leadId ? `${techNameById(leadId)} (lead)` : null,
                        ...helperIds.map((id) => techNameById(id)),
                      ]
                        .filter(Boolean)
                        .join(', ')
                    }
                  />
                )}
              </dl>

              {detail.items.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-ink-subtle mb-1">
                    Parts on this order ({detail.items.length})
                  </div>
                  <ul className="space-y-1">
                    {detail.items.map((line) => (
                      <li
                        key={line.spireId}
                        className="flex items-start justify-between gap-2 text-[12px]"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="text-ink truncate">
                            {line.description || line.partNo}
                          </div>
                          <div className="text-[10.5px] font-mono text-ink-muted">
                            {line.partNo} · {line.orderQty}
                            {line.sellMeasure ? ` ${line.sellMeasure}` : ''} × ${line.unitPrice}
                          </div>
                        </div>
                        <span className="text-[12px] tabular-nums shrink-0">
                          ${(Number(line.orderQty) * Number(line.unitPrice)).toFixed(2)}
                        </span>
                      </li>
                    ))}
                  </ul>
                  {typeof partsTotal === 'number' && (
                    <div
                      className="mt-1.5 flex items-center justify-between pt-1 text-[12px] font-medium border-t"
                      style={{ borderColor: 'var(--color-border-tertiary)' }}
                    >
                      <span>Parts subtotal</span>
                      <span className="tabular-nums">${partsTotal.toFixed(2)}</span>
                    </div>
                  )}
                </div>
              )}

              <div
                className="flex items-center justify-between pt-2 text-[12px] font-medium border-t"
                style={{ borderColor: 'var(--color-border-tertiary)' }}
              >
                <span>Order total</span>
                <span className="tabular-nums">${detail.total}</span>
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-wider text-ink-subtle mb-1">
                  Notes
                </div>
                <JobNotes spireId={detail.spireId} emptyHint="No notes from sales or the field." />
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-wider text-ink-subtle mb-1">
                  Job files
                </div>
                <JobFiles
                  spireId={detail.spireId}
                  canUpload={false}
                  emptyHint="No photos or documents attached."
                />
              </div>
            </>
          )}
        </div>

        {onStart && (
          <footer
            className="flex items-center justify-end gap-2 px-4 py-3 border-t"
            style={{ borderColor: 'var(--color-border-tertiary)' }}
          >
            <button
              type="button"
              onClick={onClose}
              className="rounded-md px-3 py-2 text-sm text-ink-muted hover:bg-surface-muted"
            >
              Close
            </button>
            <button
              type="button"
              onClick={() => {
                onClose();
                onStart();
              }}
              className="rounded-md px-3 py-2 text-sm font-medium text-white"
              style={{ backgroundColor: 'var(--color-text-info)' }}
            >
              Start job
            </button>
          </footer>
        )}
      </div>
    </div>
  );
}

function PreviewRow({
  Icon,
  label,
  value,
  href,
}: {
  Icon: typeof MapPin;
  label: string;
  value: string;
  href?: string;
}) {
  return (
    <>
      <Icon className="h-4 w-4 mt-0.5 text-ink-subtle" aria-hidden />
      <dt className="text-[10.5px] uppercase tracking-wider text-ink-subtle self-center">
        {label}
      </dt>
      {href ? (
        <a href={href} className="text-ink hover:underline truncate">
          {value}
        </a>
      ) : (
        <dd className="text-ink truncate">{value}</dd>
      )}
    </>
  );
}

/** "8a – 12p · 4h" for the assignment window. */
function formatWindow(startHour: number, duration: number): string {
  return `${windowLabel(startHour, duration)} · ${durationLabel(duration)}`;
}
